/**
 * Safely extracts jobId/vacancyId from a URL path
 * Handles various URL formats including query params, trailing slashes, and different structures
 * @param url - The URL string to parse (can be relative or absolute)
 * @returns The extracted ID or null if extraction fails
 */
export const extractIdFromUrl = (
  url: string | null | undefined,
): string | null => {
  if (!url) {
    return null;
  }

  try {
    // Handle absolute URLs by creating a URL object
    let pathname: string;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const urlObj = new URL(url);
      pathname = urlObj.pathname;
    } else {
      // For relative URLs, remove query params and hash if present
      pathname = url.split('?')[0].split('#')[0];
    }

    // Split pathname and filter out empty segments
    const segments = pathname.split('/').filter(Boolean);

    // Return the last segment if it exists, otherwise null
    if (segments.length === 0) {
      return null;
    }

    const lastSegment = segments[segments.length - 1];

    // Validate that the last segment looks like an ID (non-empty, no special chars that would indicate it's not an ID)
    // This is a basic validation - adjust based on your ID format requirements
    if (!lastSegment || lastSegment.trim() === '') {
      return null;
    }

    return lastSegment;
  } catch (error) {
    // If URL parsing fails, fall back to simple string splitting
    console.warn('Failed to parse URL:', url, error);
    const segments = url.split('/').filter(Boolean);
    return segments.length > 0 ? segments[segments.length - 1] : null;
  }
};
